<template>
    <div class="oddsTable">
        <div class="oddsTable_thead">
            <div class="oddsTable_tr">
                <div
                    v-for="header in marketHeader"
                    v-bind:key="header.title"
                    class="oddsTable_th"
                    v-bind:class="{
                        'oddsTable_th-fixed': header.isFixed,
                        'oddsTable_th-dividerRight': header.showDividerRight,
                    }"
                >
                    {{ header.title }}
                </div>
            </div>
        </div>
        <div class="oddsTable_tbody">
            <div class="oddsTable_tr">
                <StaticSpinner v-if="!loaded" />
                <template v-else>
                    <div
                        v-for="item in marketOddsDisplay"
                        v-bind:key="item.marketType"
                        class="oddsTable_td"
                        v-bind:class="{
                            'oddsTable_td-fixed': item.isFixed,
                            'oddsTable_td-dividerRight': item.showDividerRight,
                        }"
                    >
                        <div
                            v-for="list in item.oddsList"
                            v-bind:key="list.odds.id"
                            class="oddsBox"
                        >
                            <TournamentSingleOdds
                                v-for="price in list.prices"
                                v-bind:key="`${list.odds.id}-${price.option}`"
                                layout="horizontal"
                                v-bind:odds="list.odds"
                                v-bind:price="price.price"
                                v-bind:betOption="price.option"
                                v-bind:event="event"
                                v-bind:displaySection="displaySection"
                            />
                        </div>
                    </div>
                </template>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import orderBy from 'lodash/orderBy';
    import { storeToRefs } from 'pinia';
    import { computed, toRefs } from 'vue';
    import StaticSpinner from '@/components/common/StaticSpinner.vue';
    import TournamentSingleOdds from '@/components/tournamentCenter/TournamentSingleOdds.vue';
    import { getTournamentBetOptionDisplay } from '@/components/tournamentCenter/betOptionRule';
    import { Markets_Panel_Max_Odds_COUNT } from '@/components/tournamentCenter/config';
    import { i18n } from '@/core/lib/i18n';
    import { fullTimeMarketType, is1X2, sortOdds } from '@/core/lib/oddsHelper';
    import { useEventOdds } from '@/core/oddsApi/composable/useEventOdds';
    import { EnumOddsCategoryType, EnumPresetFilterType } from '@/core/oddsApi/oddsApiType';
    import { BetTypeGroup, DisplaySection, MarketType } from '@/interface/enum';
    import type { ITournamentEvent } from '@/interface/tournamentCenter';
    import { useCustomerStore } from '@/store/customerStore';

    const props = withDefaults(defineProps<{
        marketTypes: MarketType[];
        event: ITournamentEvent;
        displaySection?: DisplaySection;
    }>(), { displaySection: () => DisplaySection.Unknown });

    const marketTypeNameMapping = computed<Partial<Record<MarketType, string>>>(() => ({
        [MarketType.Handicap]: i18n.t('full_time_handicap_short'),
        [MarketType.FH_Handicap]: i18n.t('first_half_handicap_short'),
        [MarketType.OverUnder]: i18n.t('full_time_over_under_short'),
        [MarketType.FH_OverUnder]: i18n.t('first_half_over_under_short'),
        [MarketType._1X2]: i18n.t('full_time_1x2'),
        [MarketType.FH_1X2]: i18n.t('market_type_short_name_8'),
    }));

    const { marketTypes, event, displaySection } = toRefs(props);
    const marketHeader = computed(() => marketTypes.value.map((marketType, index) => ({
        title: marketTypeNameMapping.value[marketType] ?? '',
        isFixed: is1X2(marketType),
        showDividerRight: isShowDividerRight(index),
    })));

    const { priceStyle } = storeToRefs(useCustomerStore());
    const { oddsList: _oddsList, loaded } = useEventOdds({
        eventId: event.value.id,
        presetFilter: event.value.isLive ? EnumPresetFilterType.Live : EnumPresetFilterType.NonLive,
        oddsCategory: EnumOddsCategoryType.All,
        priceStyle,
        sportType: event.value.sportType,
        leagueSize: event.value.league.size,
        countBehavior: true,
        marketGroupIds: [BetTypeGroup.Main],
        marketTypes,
    });

    const orderBy1X2 = ['1', '2', 'X'];
    const orderedOddsList = computed(() => {
        const processedOddsList = _oddsList.value.map((odds) => {
            if (is1X2(odds.marketType)) {
                const sortedPrices = orderBy(odds.prices, x => orderBy1X2.indexOf(x.option.toUpperCase()));
                return { ...odds, prices: sortedPrices };
            }
            return odds;
        });

        return sortOdds(processedOddsList, event.value.sportType);
    });

    const marketOddsDisplay = computed(() => marketTypes.value.map((marketType, index) => {
        const oddsList = orderedOddsList.value
            .filter(odds => odds.marketType === marketType)
            .slice(0, Markets_Panel_Max_Odds_COUNT)
            .map(odds => ({
                odds,
                prices: odds.prices.map(price => ({
                    price,
                    option: getTournamentBetOptionDisplay(odds, price),
                })),
            }));

        return {
            marketType,
            oddsList,
            isFixed: is1X2(marketType),
            showDividerRight: isShowDividerRight(index),
        };
    }));

    const lastFullTimeIndex = computed(() => {
        for (let i = marketTypes.value.length - 1; i >= 0; i--) {
            if (fullTimeMarketType.includes(marketTypes.value[i])) {
                return i;
            }
        }
        return -1;
    });

    function isShowDividerRight(index: number): boolean {
        return index === lastFullTimeIndex.value && lastFullTimeIndex.value < marketTypes.value.length - 1;
    }

</script>

<style lang="scss" scoped>
    @import "@/components/tournamentCenter/MarketsPanel";
</style>
